<template>
  <div class="lang-switcher" ref="root">
    <button
      class="btn btn-outline lang-btn"
      type="button"
      aria-haspopup="listbox"
      :aria-expanded="open ? 'true' : 'false'"
      @click.stop="toggle"
    >
      <Icon name="material-symbols:language" size="16" />
      <span class="label label--full">{{ currentLabel }}</span>
      <span class="label label--short">{{ currentShortLabel }}</span>
      <Icon name="material-symbols:expand-more" class="dropdown-icon" />
    </button>

    <ul v-if="open" class="dropdown-menu" role="listbox">
      <li
        v-for="opt in options"
        :key="opt.code"
        role="option"
        :aria-selected="opt.code === locale"
      >
        <NuxtLink :to="pathFor(opt.code)" class="dropdown-item" @click="onSelect(opt.code)">
          <span>{{ opt.label }}</span>
          <Icon v-if="opt.code === locale" name="material-symbols:check" size="16" />
        </NuxtLink>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

const { locale, locales, t } = useI18n()
const router = useRouter()
const switchLocalePath = useSwitchLocalePath()

const emit = defineEmits<{ (e: 'changed', code: string): void }>()

const open = ref(false)
const root = ref<HTMLElement | null>(null)

// 语言选项：优先使用 nuxt.config.ts 中定义的 name，否则退化为 i18n 文案
const options = computed(() => {
  const list = (locales.value as any[]) || []
  return list.map((l) => {
    const code = l.code as string
    const label = (l.name as string) || (code === 'zh' ? t('lang.zh') : code === 'en' ? t('lang.en') : code)
    return { code, label }
  })
})

const currentLabel = computed(() => {
  const found = options.value.find((o) => o.code === locale.value)
  if (found) return found.label
  // fallback 文案
  return locale.value === 'zh' ? t('lang.zh') : locale.value === 'en' ? t('lang.en') : locale.value
})

const currentShortLabel = computed(() => {
  if (locale.value === 'zh') return t('lang.short.zh')
  if (locale.value === 'en') return t('lang.short.en')
  return (locale.value || '').toUpperCase()
})

const pathFor = (code: string) => switchLocalePath(code) || '/'
const toggle = () => (open.value = !open.value)
const close = () => (open.value = false)

const onSelect = async (code: string) => {
  try {
    await router.push(pathFor(code))
    emit('changed', code)
  } finally {
    close()
  }
}

const onClickOutside = (e: MouseEvent) => {
  const target = e.target as Node
  if (root.value && !root.value.contains(target)) close()
}

onMounted(() => document.addEventListener('click', onClickOutside))
onBeforeUnmount(() => document.removeEventListener('click', onClickOutside))
</script>

<style scoped lang="scss">
.lang-switcher { position: relative; }

.lang-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  padding: 6px 10px; /* 比默认按钮更紧凑，缓解拥挤 */
}

/* 轮廓按钮外观（与头部样式保持一致风格） */
.btn.btn-outline {
  background-color: transparent;
  border: 1px solid #e5e7eb;
  color: #374151;
}
.btn.btn-outline:hover {
  border-color: var(--color-primary, #3b82f6);
  color: var(--color-primary, #3b82f6);
}

.dropdown-icon {
  font-size: 16px;
  transition: transform 0.2s ease;
}

.dropdown-menu {
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  background-color: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.08);
  min-width: 160px;
  padding: 6px 0;
  z-index: 1000;
}

.dropdown-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  color: #374151;
  text-decoration: none;
}

.dropdown-item:hover {
  background: #f3f4f6;
  color: #111827;
}

/* 默认显示全称，隐藏短码 */
.label--short { display: none; }

/* 中等屏幕起：显示短码，隐藏全称，减小占位 */
@media (max-width: 1200px) {
  .label--full { display: none; }
  .label--short { display: inline; }
}

/* 非常窄的屏幕：全部隐藏，仅保留图标 */
@media (max-width: 480px) {
  .label--full, .label--short { display: none; }
}
</style>
